import * as React from 'react'
import Loading from '@doordash/component-loading'
import { Inset, StackChildren, Text, Spacing } from '@doordash/design-language'
import Separator from '@doordash/component-separator'
import { TemplateResponseData, SectionProps, Page } from './types'
import { clone, isEqual, keys, setWith } from 'lodash'
import { Section } from './Section'
import { StickyBottom } from '../../atoms/sticky-bottom'
import { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { FooterSection } from './FooterSection'
import { BackButton } from './BackButton'
import { ValidationProvider } from './useValidation'
import { getIsMobile } from '../../../utilities/userAgent'
import { JSONSchemaType } from 'ajv'
import { SubmitOnboardingCallback } from '../../pages/onboarding-workflow/OnboardingWorkflow'

export const FullHeightWrapper = styled.div`
  min-height: ${(props: { isMobile: boolean }) =>
    props.isMobile ? 'calc(100vh - 84px)' : 'auto'};
  position: relative;
`

type WorkflowTemplateProps = {
  currentPageTemplate: Page
  onSubmitStep: SubmitOnboardingCallback
  trackWorkflowStepRendered: () => void
}

export const WorkflowTemplate: React.FC<WorkflowTemplateProps> = ({
  currentPageTemplate,
  onSubmitStep,
  trackWorkflowStepRendered,
}) => {
  const [templateResponseData, setTemplateResponseData] = React.useState<
    TemplateResponseData
  >(currentPageTemplate.template_response_data)

  const [emptyContainerHeight, setEmptyContainerHeight] = useState('0px')
  const emptyRef = useRef(null)
  const footerRef = useRef(null)

  useEffect(() => {
    setTemplateResponseData(currentPageTemplate.template_response_data)
    trackWorkflowStepRendered()
  }, [currentPageTemplate, trackWorkflowStepRendered])

  useEffect(() => {
    if (footerRef && footerRef.current) {
      setEmptyContainerHeight(`${footerRef.current.offsetHeight}px`)
    }
  }, [currentPageTemplate, footerRef])

  const updateData = (path: string, value) => {
    setTemplateResponseData(currentData =>
      setWith(clone(currentData), path, value, clone)
    )
  }

  if (
    !isEqual(
      keys(templateResponseData),
      keys(currentPageTemplate.template_response_data)
    )
  ) {
    return <Loading />
  }

  const mainSections: SectionProps[] = currentPageTemplate.sections.filter(
    section => {
      return !section.type || section.type !== 'FOOTER'
    }
  )

  const footerSection: SectionProps = currentPageTemplate.sections.filter(
    section => {
      return section.type === 'FOOTER'
    }
  )[0]

  const isMobile = getIsMobile()
  const validationSchema =
    currentPageTemplate.validation_schema ||
    (({} as unknown) as JSONSchemaType<TemplateResponseData>)

  return (
    <ValidationProvider schema={validationSchema}>
      <FullHeightWrapper isMobile={isMobile}>
        <Inset size={Inset.Sizes.Large}>
          <StackChildren size={StackChildren.Sizes.Large}>
            <StackChildren size={StackChildren.Sizes.XxSmall}>
              {currentPageTemplate.back_button_data && (
                <BackButton
                  backButtonData={currentPageTemplate.back_button_data}
                  onSubmitStep={onSubmitStep}
                />
              )}
              {currentPageTemplate.header && (
                <Text styles={Text.Styles.PageTitle2}>
                  {currentPageTemplate.header}
                </Text>
              )}
              {currentPageTemplate.sub_header && (
                <Text styles={Text.Styles.Body2}>
                  {currentPageTemplate.sub_header}
                </Text>
              )}
            </StackChildren>
            {mainSections.map((section: SectionProps, index: number) => (
              <div key={index}>
                <Section
                  section={section}
                  onDataUpdate={updateData}
                  templateResponseData={templateResponseData}
                  onSubmitStep={onSubmitStep}
                />
                {index !== mainSections.length - 1 && (
                  // TODO: add condition to remove separator between sections here
                  <Separator
                    type={Separator.Types.FullBorder}
                    separatorHeight={Spacing.Small}
                    verticalMargin={Spacing.Large}
                  />
                )}
              </div>
            ))}
          </StackChildren>
          {isMobile &&
            ((footerSection && footerSection.components.length > 0) ||
              currentPageTemplate.cta_buttons.length > 0) && (
              <div ref={emptyRef} style={{ height: emptyContainerHeight }} />
            )}
        </Inset>
        {((footerSection && footerSection.components.length > 0) ||
          currentPageTemplate.cta_buttons.length > 0) && (
          <StickyBottom isMobile={isMobile}>
            <div ref={footerRef}>
              <FooterSection
                section={footerSection}
                onDataUpdate={updateData}
                buttons={currentPageTemplate.cta_buttons}
                templateResponseData={templateResponseData}
                onSubmitStep={onSubmitStep}
              />
            </div>
          </StickyBottom>
        )}
      </FullHeightWrapper>
    </ValidationProvider>
  )
}
